import React from 'react';

import Button from '@/core/components/Button';
import { UserStatusSection } from '@/core/components/ProfileDetails/UserStatusSection';
import useDisclosure from '@/core/hooks/useDisclosure';
import type UserDomain from '@/core/lib/new-architecture/domain/user.domain';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { SuperUserModal } from '@/core/pages/profile/PublicProfile/Status/SuperUserModal';

export const StatusPanel: React.FC<{ user: UserDomain }> = ({ user }) => {
  const { t } = useTranslationContext(['common', 'profile.profile-public']);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { points, percent } = user.activityPoints;
  const { isSuperUser, isNice, isPunctual } = user.status;

  return (
    <>
      <UserStatusSection showInactive isNice={isNice} isPunctual={isPunctual} isSmall={false} isSuperUser={isSuperUser} />
      <hr className="border-stroke-secondary" />
      <div className="space-y-2">
        <div className="text-body-secondary font-medium">{t('status.super-user.title', { ns: 'profile.profile-public' })}</div>
        <p className="text-caption-primary text-content-placeholder">{t('status.super-user.points', { ns: 'profile.profile-public', activityPoints: points.all, count: points.superUserThreshold })}</p>
      </div>
      <div className="text-caption-primary space-y-1">
        <ProgressBar
          background="#7D7D8D"
          globalPercent={percent.all}
          sections={[
            { className: 'bg-green-700', percent: percent.notExpSoon },
            { className: 'bg-orange-500', percent: percent.expSoon },
          ]}
        />
        <Legend {...points} />
      </div>
      {!!points.missing && (
        <div className="text-caption-primary rounded-1 bg-brand-alt-50 p-2 text-content-secondary lg:p-3">
          {t('status.super-user.missing-hint', { ns: 'profile.profile-public', count: points.missing })}
        </div>
      )}
      <Button variant="secondary" size="medium" iconLeft="info" onClick={onOpen}>
        {t('status.super-user.button', { ns: 'profile.profile-public' })}
      </Button>
      <SuperUserModal isOpen={isOpen} onClose={onClose} user={user} />
    </>
  );
};

const Dot: React.FC<{ className: string }> = ({ className }) => <div className={`size-[10px] rounded-full ${className}`} />;

const ProgressBar: React.FC<{ background: string; globalPercent: number; sections: { className: string; percent: number }[] }> = ({ background, globalPercent, sections }) => (
  <div className="relative h-5 overflow-hidden rounded-10 lg:h-7" style={{ background }}>
    <div className="absolute inline-flex h-full" style={{ width: `${globalPercent}%` }}>
      <div className={`absolute top-0 grid h-full items-center font-semibold text-[#FFF] ${globalPercent > 10 ? 'right-3' : '-right-8'}`}>{globalPercent}%</div>
    </div>
    {sections.map(({ className, percent }) => (
      <div key={className} className={`h-full transition ${className}`} style={{ width: `${percent}%` }} />
    ))}
  </div>
);

const Legend: React.FC<UserDomain['activityPoints']['points']> = ({ notExpSoon, expSoon, missing }) => {
  const { t } = useTranslationContext(['common', 'profile.profile-public']);

  return (
    <div className="flex justify-between text-content-placeholder">
      <div className="flex flex-wrap gap-x-4">
        <div className="flex items-center gap-2">
          <Dot className="bg-green-700" />
          <p>{t('status.super-user.not-exp-soon', { ns: 'profile.profile-public', count: notExpSoon })}</p>
        </div>
        {!!expSoon && (
          <div className="flex items-center gap-2">
            <Dot className="bg-orange-500" />
            <p>{t('status.super-user.exp-soon', { ns: 'profile.profile-public', count: expSoon })}</p>
          </div>
        )}
      </div>
      {!!missing && <div className="text-nowrap">{t('status.super-user.missing', { ns: 'profile.profile-public', count: missing })}</div>}
    </div>
  );
};
